/**
 * /admin/reels/links — multi-use expirable share links only.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Copy, Link2, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { PrivateReelsShareLink } from "./types";

export default function PrivateReelsLinksPage() {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState<PrivateReelsShareLink[]>([]);
  const [linkLabel, setLinkLabel] = useState("");
  const [linkTtl, setLinkTtl] = useState("3600");
  const [linkTtlMode, setLinkTtlMode] = useState<"preset" | "custom">("preset");
  const [linkCustomMinutes, setLinkCustomMinutes] = useState("30");
  const [linkMaxUses, setLinkMaxUses] = useState("5");
  const [creatingLink, setCreatingLink] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/private-reels-links", { headers });
      const data = (await res.json()) as {
        ok?: boolean;
        links?: PrivateReelsShareLink[];
        error?: string;
      };
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setLinks(data.links || []);
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const resolveTtlSeconds = (): number => {
    if (linkTtlMode === "custom") {
      const mins = Math.max(1, Math.min(30 * 24 * 60, Number(linkCustomMinutes) || 30));
      return mins * 60;
    }
    return Math.max(60, Number(linkTtl) || 3600);
  };

  const createLink = async () => {
    setCreatingLink(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/private-reels-links", {
        method: "POST",
        headers,
        body: JSON.stringify({
          label: linkLabel.trim() || null,
          ttlSeconds: resolveTtlSeconds(),
          maxUses: Number(linkMaxUses) || 0,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        link?: PrivateReelsShareLink;
        error?: string;
      };
      if (!res.ok || !data.ok || !data.link) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setLinks((prev) => [data.link!, ...prev]);
      setLinkLabel("");
      toast({ title: "Link creado" });
      try {
        await navigator.clipboard.writeText(data.link.url);
        toast({ title: "URL copiada" });
      } catch {
        /* ignore */
      }
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Error",
        variant: "destructive",
      });
    } finally {
      setCreatingLink(false);
    }
  };

  const revokeLink = async (id: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/private-reels-links", {
        method: "POST",
        headers,
        body: JSON.stringify({ revokeId: id }),
      });
      const data = (await res.json()) as { ok?: boolean };
      if (!res.ok || !data.ok) throw new Error("No se pudo revocar");
      setLinks((prev) =>
        prev.map((l) => (l.id === id ? { ...l, status: "revoked" } : l)),
      );
      toast({ title: "Link revocado" });
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Error",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando links…
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-white/[0.08] bg-zinc-900/40 p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Link2 className="h-4 w-4" /> Links multi-uso
        </h2>
        <Button type="button" size="sm" variant="ghost" onClick={() => void load()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Formato: <code className="text-[10px]">/reels?access=TOKEN</code>. Abre
        el <strong className="text-white/70">feed privado completo</strong>{" "}
        (live). Para <strong className="text-white/70">elegir videos</strong>,
        TTL en segundos y link que se quema →{" "}
        <Link className="underline" to="/admin/reels/secret-share">
          Secret share
        </Link>
        .
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label>Etiqueta</Label>
          <Input
            value={linkLabel}
            onChange={(e) => setLinkLabel(e.target.value)}
            placeholder="Cliente VIP / press"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Expiración</Label>
          <Select
            value={linkTtlMode === "custom" ? "custom" : linkTtl}
            onValueChange={(v) => {
              if (v === "custom") setLinkTtlMode("custom");
              else {
                setLinkTtlMode("preset");
                setLinkTtl(v);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="300">5 minutos</SelectItem>
              <SelectItem value="900">15 minutos</SelectItem>
              <SelectItem value="1800">30 minutos</SelectItem>
              <SelectItem value="3600">1 hora</SelectItem>
              <SelectItem value="21600">6 horas</SelectItem>
              <SelectItem value="86400">24 horas</SelectItem>
              <SelectItem value="604800">7 días</SelectItem>
              <SelectItem value="custom">Custom (minutos)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {linkTtlMode === "custom" && (
          <div className="space-y-1.5">
            <Label>Minutos</Label>
            <Input
              type="number"
              min={1}
              max={43200}
              value={linkCustomMinutes}
              onChange={(e) => setLinkCustomMinutes(e.target.value)}
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Máx. usos (0 = ∞)</Label>
          <Input
            type="number"
            min={0}
            value={linkMaxUses}
            onChange={(e) => setLinkMaxUses(e.target.value)}
          />
        </div>
      </div>

      <Button type="button" onClick={() => void createLink()} disabled={creatingLink}>
        {creatingLink ? (
          <Loader2 className="h-4 w-4 animate-spin mr-1" />
        ) : (
          <Plus className="h-4 w-4 mr-1" />
        )}
        Crear link
      </Button>

      <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
        <table className="w-full text-left text-[11px]">
          <thead className="bg-white/[0.03] text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Estado</th>
              <th className="px-3 py-2 font-medium">Etiqueta</th>
              <th className="px-3 py-2 font-medium">Usos</th>
              <th className="px-3 py-2 font-medium">Último uso</th>
              <th className="px-3 py-2 font-medium">Expira</th>
              <th className="px-3 py-2 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {links.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-muted-foreground">
                  Sin links todavía.
                </td>
              </tr>
            )}
            {links.map((l) => (
              <tr key={l.id} className="border-t border-white/[0.05]">
                <td className="px-3 py-2">
                  <Badge
                    variant={l.status === "active" ? "default" : "outline"}
                    className="text-[10px]"
                  >
                    {l.status}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{l.label || "—"}</div>
                  <div className="max-w-[200px] truncate font-mono text-[9px] text-muted-foreground">
                    {l.url}
                  </div>
                </td>
                <td className="px-3 py-2 tabular-nums">
                  {l.uses_count}/{l.max_uses === 0 ? "∞" : l.max_uses}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {l.last_used_at
                    ? new Date(l.last_used_at).toLocaleString()
                    : "nunca"}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {new Date(l.expires_at).toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(l.url);
                          toast({ title: "URL copiada" });
                        } catch {
                          toast({
                            title: "No se pudo copiar",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    {l.status === "active" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => void revokeLink(l.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
